import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { PROJECT } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import DiagnoseWorkspace from "@/pages/workspaces/DiagnoseWorkspace";
import WorkOrdersWorkspace from "@/pages/workspaces/WorkOrdersWorkspace";
import PerformanceWorkspace from "@/pages/workspaces/PerformanceWorkspace";
import ValueCalcWorkspace from "@/pages/workspaces/ValueCalcWorkspace";
import EvidenceWorkspace from "@/pages/workspaces/EvidenceWorkspace";
import ScoreWorkspace from "@/pages/workspaces/ScoreWorkspace";
import { Stethoscope, ClipboardList, Activity, Calculator, FolderCheck, MapPin, FileText, Gauge } from "lucide-react";

const WS = [
  { key: "score", label: "ROOTS Score", icon: Gauge },
  { key: "diagnose", label: "Diagnose", icon: Stethoscope },
  { key: "work-orders", label: "Work Orders", icon: ClipboardList },
  { key: "performance", label: "Performance", icon: Activity },
  { key: "value-calc", label: "Value Calculator", icon: Calculator },
  { key: "evidence", label: "Evidence Centre", icon: FolderCheck },
];

export default function ProjectPage() {
  const ws = useWorkspace();
  const { hasRole } = useAuth();
  const [sp] = useSearchParams();
  const visibleWS = hasRole("auditor") ? WS.filter((w) => ["score", "evidence"].includes(w.key)) : WS;
  const [tab, setTab] = useState(() => {
    const t = sp.get("tab");
    return visibleWS.some((w) => w.key === t) ? t : "score";
  });

  if (!ws.site && !ws.contract) {
    return (
      <div className="rounded-xl border border-dashed border-sage-light bg-white p-10 text-center">
        <p className="font-heading text-lg font-semibold text-forest-dark">Pick a site and contract</p>
        <p className="mt-1 text-sm text-muted-foreground">Use the selectors at the top to choose which record to work in. Add sites in Setup & Data if you have none.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid={PROJECT.root}>
      <div className="rounded-2xl border border-sage-light bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-forest-dark">{ws.site?.name || "—"}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ws.site?.location || "—"}</span>
              <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {ws.contract?.name || "No contract"}</span>
              {ws.contract?.end_date && <span>Ends {fmtDate(ws.contract.end_date)}</span>}
            </div>
          </div>
          {ws.contract && <StatusBadge status={ws.contract.status} />}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          {visibleWS.map((w, i) => (
            <TabsTrigger key={w.key} value={w.key} data-testid={PROJECT.tab(w.key)} className="gap-1.5">
              <w.icon className="h-4 w-4" /> <span className="hidden sm:inline">{i + 1}.</span> {w.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="score" className="mt-4"><ScoreWorkspace /></TabsContent>
        <TabsContent value="diagnose" className="mt-4"><DiagnoseWorkspace /></TabsContent>
        <TabsContent value="work-orders" className="mt-4"><WorkOrdersWorkspace /></TabsContent>
        <TabsContent value="performance" className="mt-4"><PerformanceWorkspace /></TabsContent>
        <TabsContent value="value-calc" className="mt-4"><ValueCalcWorkspace /></TabsContent>
        <TabsContent value="evidence" className="mt-4"><EvidenceWorkspace /></TabsContent>
      </Tabs>
    </div>
  );
}
